import { useMemo } from 'react';
import type { FC } from 'react';
import { useBreakpointValue } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { ConditionalRender } from '../../../../components/conditionally-render/ConditionalRender';
import { GetIcon } from '../../../../components/icons';
import { SoftLink } from '../../../../components/soft-link/SoftLink';
import { Text } from '../../../../ui-components';
import { BaseSwatchesList } from '../../../../ui-components/variant/swatch/swatches-list/BaseSwatchesList';
import { classNames } from '../../../../utils/tailwind/classNames';
import type { SearchVariant } from '../../hooks/use-search';

export interface SelectedVariant {
    id: string;
    c_pwaUrl: string;
    c_imageUrl: string;
}

export interface PdpSwatchesListProps {
    currentSelection: SelectedVariant;
    variants: SearchVariant[];
    activeId?: string;
    onChange: (activeProductId: string) => void;
    size?: 'small' | 'default';
    onMouseEnter?: (productId?: string) => void;
    onMouseLeave?: (productId?: string) => void;
    className?: string;
}

export const PlpSwatchesList: FC<PdpSwatchesListProps> = ({
    currentSelection,
    variants,
    activeId,
    onChange,
    size = 'default',
    onMouseEnter,
    onMouseLeave,
    className,
}) => {
    const swatches = useMemo(
        () =>
            variants.map((variant) => ({
                color: variant.swatchColor ?? undefined,
                colorName: variant.colorName,
                productId: variant.id,
            })),
        [variants],
    );

    const maxSwatchesToShow =
        useBreakpointValue({ base: 3, sm: 4, lg: 6, xl: 8 }) ?? variants.length;
    const showMoreButton = variants.length > maxSwatchesToShow;

    return (
        <div className={classNames('flex items-center gap-1', className)}>
            <BaseSwatchesList
                id={`plp-${currentSelection.id}`}
                swatches={swatches}
                activeId={activeId}
                onChange={onChange}
                size={size}
                renderFirstX={maxSwatchesToShow}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            />
            <ConditionalRender visibility={showMoreButton}>
                <SoftLink
                    to={currentSelection.c_pwaUrl}
                    className="flex items-center gap-[2px]"
                    data-testid="plp-swatches-list-more-button"
                >
                    <GetIcon name="plus" size="sm" />
                    <Text size="xs" color="slt-pepper.1000">
                        <FormattedMessage id="plp.swatches.more" defaultMessage="More" />
                    </Text>
                </SoftLink>
            </ConditionalRender>
        </div>
    );
};

PlpSwatchesList.defaultProps = {
    activeId: undefined,
    size: 'default',
    onMouseEnter: undefined,
    onMouseLeave: undefined,
    className: undefined,
};
